import React, { useEffect, useRef, useState } from "react";
import { Stage1ConfirmQuestions } from "./Stage1ConfirmQuestions";
import { SkipComplianceModal } from "./SkipComplianceModal";
import { SkippedComplianceSummary } from "./SkippedComplianceSummary";

/** The two stages of Step 0. Stage 2 (the grid) is delivered by Task 009. */
export type PreComplianceStage = 1 | 2;

/**
 * Task 009 plugs the Stage 2 compliance grid in here. `PreComplianceStep`
 * renders `<Stage2ComplianceGrid rfxId={rfxId} onBack={...} onComplete={...} />`
 * via this prop once stage === 2; until then a placeholder is shown.
 *
 * Hand-off contract (Stage 1 → Stage 2):
 *  - Stage 1 saves the RFx-scoped questions (PUT) before calling `onProceed`,
 *    so by the time Stage 2 mounts the `["compliance", rfxId, "questions"]`
 *    cache is fresh and the grid can read the saved set (or its own GET grid).
 *  - `onComplete` should advance the stepper past Step 0 (same effect as skip).
 *  - `onBack` returns to Stage 1.
 */
export interface Stage2GridProps {
	rfxId: number;
	onBack: () => void;
	onComplete: () => void;
}

/**
 * How Step 0 was resolved:
 *  - "grid" → the lead completed the compliance grid and clicked Proceed. The
 *    backend has NOT yet advanced; the parent must call advance-step.
 *  - "skip" → the lead skipped compliance. The skip endpoint already resolves
 *    Pre-Compliance server-side (lands the eval on Criteria), so the parent must
 *    NOT call advance-step (doing so overshoots into criteria validation → 500).
 */
export type PreComplianceCompletionSource = "grid" | "skip";

export interface PreComplianceStepProps {
	rfxId: number;
	/** Advance the parent stepper past Step 0 (skip-confirmed or grid-complete). */
	onStepComplete: (source: PreComplianceCompletionSource) => void;
	/**
	 * Extension point for Task 009. When provided it is rendered for stage 2;
	 * otherwise a placeholder is shown. Wired so Task 009 only needs to supply
	 * its grid component without touching this container's stage logic.
	 */
	renderStage2?: (props: Stage2GridProps) => React.ReactNode;
	/**
	 * Pre-Compliance has already been resolved (proceeded via the grid, skipped,
	 * or locked by scoring). When the user navigates back to Step 0 in this state
	 * we open directly on the completed compliance grid (Stage 2) rather than the
	 * editable questions setup (Stage 1) — the questions were already confirmed,
	 * so re-presenting the "Proceed / Skip" setup is misleading (PRO-575).
	 *
	 * NOTE: a SKIPPED step is also "completed" but is handled separately — see
	 * `skipped` below — so it shows the skipped summary, not the grid.
	 */
	completed?: boolean;
	/**
	 * Compliance was SKIPPED. Step 0 then shows the skipped summary (with a
	 * "Start Compliance Check" action) instead of the auto-evaluated grid.
	 */
	skipped?: boolean;
}

export const PreComplianceStep = ({
	rfxId,
	onStepComplete,
	renderStage2,
	completed = false,
	skipped = false,
}: PreComplianceStepProps) => {
	// Skipped takes precedence: never auto-open the grid for a skipped step.
	const openGridInitially = completed && !skipped;
	const [stage, setStage] = useState<PreComplianceStage>(
		openGridInitially ? 2 : 1
	);
	const [isSkipModalOpen, setIsSkipModalOpen] = useState(false);

	// `completed` is driven by an async status query, so it may flip true after
	// mount (initial render happens before the query resolves). Open the grid once
	// when that happens. A ref makes this a one-shot: after the auto-open the user
	// can still use the grid's "Back" to review/edit the questions without being
	// bounced straight back to the grid on the next render.
	const didAutoOpenGrid = useRef(false);
	useEffect(() => {
		if (openGridInitially && !didAutoOpenGrid.current) {
			didAutoOpenGrid.current = true;
			setStage(2);
		}
	}, [openGridInitially]);

	const handleProceed = () => {
		setStage(2);
	};

	const handleSkipConfirmed = () => {
		setIsSkipModalOpen(false);
		onStepComplete("skip");
	};

	// Skipped: show the skipped summary (with "Start Compliance Check") rather than
	// the editable setup or the auto-evaluated grid. Re-opening clears the skip
	// server-side, after which `skipped` flips false and the normal stages render.
	if (skipped) {
		return (
			<div data-testid="pre-compliance-step">
				<SkippedComplianceSummary rfxId={rfxId} />
			</div>
		);
	}

	return (
		<div data-testid="pre-compliance-step">
			{stage === 1 && (
				<Stage1ConfirmQuestions
					rfxId={rfxId}
					onProceed={handleProceed}
					onSkip={() => setIsSkipModalOpen(true)}
				/>
			)}

			{stage === 2 &&
				(renderStage2 ? (
					renderStage2({
						rfxId,
						onBack: () => setStage(1),
						onComplete: () => onStepComplete("grid"),
					})
				) : (
					<div
						className="rounded-md border border-dashed border-gray-200 py-10 text-center text-sm text-gray-500"
						data-testid="pre-compliance-stage-2-placeholder"
					>
						Compliance grid coming soon.
					</div>
				))}

			<SkipComplianceModal
				rfxId={rfxId}
				isOpen={isSkipModalOpen}
				onClose={() => setIsSkipModalOpen(false)}
				onConfirmed={handleSkipConfirmed}
			/>
		</div>
	);
};
